import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { CountBars } from '../components/CountBars';
import { Pill } from '../components/Pill';
import { fmtDollars, fmtNum } from '../lib/format';

export function OverviewScreen() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: () => insightsApi.overview() });
  const series = useQuery({ queryKey: ['users-series', 12], queryFn: () => insightsApi.usersSeries(12) });

  const o = overview.data;
  const bars = (series.data ?? []).map((p) => ({ label: p.label, value: p.count, inset: p.premiumCount }));

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 14,
        }}
      >
        <KpiCard label="Total users" value={o?.users.total ?? 0} accent />
        <KpiCard label="New this month" value={o?.users.newThisMonth ?? 0} />
        <KpiCard
          label="Premium users"
          value={o?.users.premium ?? 0}
          sublabel={o ? `${o.users.conversionPct}% conversion` : undefined}
        />
        <KpiCard
          label="Portfolio value"
          value={o ? parseFloat(o.collections.aggregatePortfolioValue) : 0}
          format={(n) => fmtDollars(n)}
          sublabel="latest snapshot · all collections"
        />
        <KpiCard label="Collections" value={o?.collections.totalCollections ?? 0} />
        <KpiCard label="Cards held" value={o?.collections.totalItems ?? 0} />
      </div>

      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Signups · last 12 months
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', display: 'inline-flex', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand-soft)' }} /> all
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand)' }} /> premium
            </span>
          </span>
        </div>
        <CountBars data={bars} />
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            Platform split
          </div>
          {(o?.platformSplit ?? []).map((p) => (
            <Row key={p.platform} label={p.platform} value={fmtNum(p.count)} />
          ))}
        </Card>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            Top countries
          </div>
          {(o?.countrySplit ?? []).map((c) => (
            <Row key={c.country ?? 'unknown'} label={c.country ?? '—'} value={fmtNum(c.count)} mono />
          ))}
          {o && o.countrySplit.length === 0 && (
            <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No country data.</span>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {label}
      </span>
      <Pill size="sm">{value}</Pill>
    </div>
  );
}
